import type { Editor } from '@tiptap/react';
import type { DocumentTextSelection, DocumentTextSelectionPayload } from '~/types/planning';

const MIN_DOCUMENT_SELECTION_LENGTH = 2;

export const createDocumentSelectionId = (): string =>
  `selection-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const getEditorTextSelection = (editor: Editor): string | null => {
  const { from, to, empty } = editor.state.selection;
  if (empty || from === to) {
    return null;
  }

  const text = editor.state.doc.textBetween(from, to, '\n\n').trim();
  if (text.length < MIN_DOCUMENT_SELECTION_LENGTH) {
    return null;
  }

  return text;
};

export const isSelectionInCollapsibleSection = (
  editor: Editor,
  sectionType: 'goal' | 'outline',
): boolean => {
  const { $from } = editor.state.selection;

  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth);
    if (node.type.name === 'collapsibleSection' && node.attrs.sectionType === sectionType) {
      return true;
    }
  }

  return false;
};

const toDocumentTextSelectionPayload = (
  selection: DocumentTextSelection,
): DocumentTextSelectionPayload => ({
  sectionType: selection.sectionType,
  selectedText: selection.text,
  canvasId: selection.canvasId,
  chapterId: selection.chapterId,
});

export const getDocumentSelectionChipLabel = (selection: DocumentTextSelection): string => {
  const prefix = selection.sectionType === 'GOAL' ? 'Goal' : 'Outline';
  const preview =
    selection.text.length > 28 ? `${selection.text.slice(0, 28)}…` : selection.text;
  return `${prefix}: ${preview}`;
};

export const buildPromptWithDocumentSelection = (
  userPrompt: string,
  selection: DocumentTextSelection | null,
): string => {
  if (!selection) {
    return userPrompt.trim();
  }

  const scopeLabel =
    selection.sectionType === 'GOAL'
      ? 'the goal document'
      : 'the outline canvas content';

  const selectedBlock = `The user selected this passage from ${scopeLabel}:\n\n"""${selection.text}"""`;
  const instruction = userPrompt.trim();

  if (!instruction) {
    return `${selectedBlock}\n\nEdit only the selected passage. Apply a focused rewrite that preserves the surrounding document context.`;
  }

  return `${selectedBlock}\n\nUser instruction: ${instruction}`;
};
